import React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Box,
  Grid,
  Backdrop,
  CircularProgress,
  IconButton
} from "@mui/material";
import {
  makeStyles,
  withStyles
} from "@mui/styles";
import {HelpOutlineOutlined, Close} from "@mui/icons-material";
import { Formik } from "formik";
import * as Yup from "yup";
import { useSelector } from "react-redux";
import agent from "../../../../../agent/agent";
import { notification, notificationTypes } from "../../../../../utils/notification";
import getTrans from "../../../../../languages/getTrans";

const isBotEmail = (email = "") => String(email).includes("bot@grizzlysms.com");

const getInitialEmail = (profile) => {
  const email = profile?.email || "";
  return isBotEmail(email) ? "" : email;
};

const FormFeedback = () => {
  const classes = useStyles();
  const profile = useSelector((state) => state?.profile?.user);
  const initialEmail = getInitialEmail(profile);

  const [isOpen, setIsOpen] = React.useState(false);
  const handleChangeOpen = (_val) => {
    setIsOpen(_val);
  };

  const [isLoading, setLoading] = React.useState(false);
  const handleClose = () => {
    if (!isLoading) {
      setIsOpen(false);
    }
  };

  const handleSubmitFormData = async (values, formikProps) => {
    setLoading(true);

    const body = {
      "type": getTrans('formFeedback.Обратная связь'),
      "name": profile?.email || "",
      "phone": profile?.phone || "",
      "surname": values?.name || "",
      "message": values?.message,
      "email": values?.email || "",
      "source": "tg-app",
      "nophone": 1,
      "g-recaptcha-response": "g-recaptcha-response"
    };
    const res = await agent.post('/api/form-api/send', body).then((res) => {
      return res.data
    }).catch((err) => {
      return {error: getTrans('errors.Ошибка сервера')}
    });
    if (res?.error) {
      setLoading(false);
      notification({message: res?.error, type: notificationTypes.error});
      return
    }

    formikProps.resetForm({
      values: {
        name: "",
        email: initialEmail,
        message: "",
      }
    });
    setLoading(false);
    notification({
      type: notificationTypes.success,
      message: getTrans('feedback.Обращение успешно отправлено')
    });
    setIsOpen(false);
  };

  return (
    <div>
      <div className={classes.title}>{getTrans('formFeedback.Есть какие-то сложности или вопросы?')}</div>
      <Button fullWidth className={classes.buttonOpenForm} onClick={handleChangeOpen.bind(null, true)}>
        {getTrans('formFeedback.Написать в поддержку')}
        <HelpOutlineOutlined sx={{fontSize: 20}}/>
      </Button>

      <Dialog
        open={isOpen}
        onClose={handleClose}
        className={classes.dialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle className={classes.dialogTitle}>
          {getTrans('formFeedback.Напишите ваше сообщение')}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            className={classes.closeButton}
            disabled={isLoading}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Formik
            enableReinitialize
            initialValues={{
              name: "",
              email: initialEmail,
              message: "",
            }}
            validationSchema={validationSchema}
            onSubmit={handleSubmitFormData}
          >
            {(params) => {
              const {
                values,
                errors,
                touched,
                handleSubmit,
                handleChange,
                handleBlur,
              } = params;

              return (
                <>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <div className={classes.formFieldName}>{getTrans('formFeedback.Ваше имя')}</div>
                      <TextField
                        value={values.name}
                        fullWidth={true}
                        variant="outlined"
                        name="name"
                        size="small"
                        placeholder={getTrans('formFeedback.Как к вам обращаться')}
                        error={Boolean(touched.name && errors.name)}
                        helperText={touched.name && errors.name}
                        className={classes.textField}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <div className={classes.formFieldName}>{getTrans('forms.Email')}</div>
                      <TextField
                        value={values.email}
                        fullWidth={true}
                        variant="outlined"
                        name="email"
                        size="small"
                        placeholder={getTrans('forms.Email')}
                        error={Boolean(touched.email && errors.email)}
                        helperText={touched.email && errors.email}
                        className={classes.textField}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <div className={classes.formFieldName}>{getTrans('formFeedback.Ваше сообщение')}</div>
                      <TextField
                        rows={4}
                        value={values.message}
                        multiline={true}
                        fullWidth={true}
                        name="message"
                        size="small"
                        placeholder={getTrans('formFeedback.Введите сообщение')}
                        variant="outlined"
                        error={Boolean(touched.message && errors.message)}
                        helperText={touched.message && errors.message}
                        className={classes.textField}
                        onChange={handleChange}
                        onBlur={handleBlur}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        fullWidth={true}
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={isLoading}
                        className={classes.submitButton}
                      >
                        {getTrans('formFeedback.Отправить сообщение')}
                      </Button>
                    </Grid>
                  </Grid>
                  <Backdrop open={isLoading} className={classes.backdrop}>
                    <CircularProgress/>
                  </Backdrop>
                </>
              )
            }}
          </Formik>
        </DialogContent>
      </Dialog>
    </div>
  )
}

const useStyles = makeStyles(theme => ({
  title: {
    opacity: "0.5",
    marginBottom: "4px",
    fontSize: 15,
    color: "white",
    lineHeight: "18px"
  },
  buttonOpenForm: {
    gap: "8px",
    height: 40,
    display: "flex",
    marginTop: "8px",
    alignItems: "center",
    justifyContent: "center",
    background: "#FB780033!important",
    fontSize: 15,
    color: "white",
    lineHeight: "18px"
  },


  dialog: {
    "& .MuiPaper-root": {
      margin: "0",
      width: "100%",
      background: "#0F191F",
      boxSizing: "border-box",
      borderRadius: "12px 12px 0 0",
      boxShadow: "0px -8px 24px 0px #0000003D",
    },
    "& .MuiBackdrop-root": {
      backgroundColor: "transparent!important",
    },
    "& .MuiDialog-container": {
      width: "100%",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "initial",
    },
    "& .MuiDialogContent-root": {
      boxSizing: "border-box",
    }
  },
  dialogTitle: {
    display: "flex",
    alignItems: "center",
    padding: "14px 16px",
    paddingBottom: 0,
    justifyContent: "space-between",

    fontSize: 20,
    color: "white",
    fontWeight: "500",
    lineHeight: "24px",
  },
  dialogContent: {
    padding: "14px 16px",
    paddingTop: "14px!important",
  },
  closeButton: {
    color: "white",
    padding: "4px"
  },
  textField: {
    "& .MuiInputBase-root": {
      background: "#FFFFFF0D",
      border: "1px solid #FFFFFF1A",
    },
    "& .MuiInputLabel-root": {
      color: "rgba(255, 255, 255, 0.7)",
    },
    "& .MuiOutlinedInput-input": {
      color: "white",
      "&::placeholder": {
        opacity: 0.5,
        color: "rgba(255, 255, 255, 0.7)",
      },
    },
  },
  submitButton: {
    height: "48px",
    background: "#FB7800!important",
    fontSize: 16,
    fontWeight: "500",
    color: "white",
    "&:hover": {
      background: "#FB7800!important",
    },
    "&.Mui-disabled": {
      background: "#8E8E8E!important",
      color: "#FFFFFF"
    }
  },
  formFieldName: {
    opacity: 0.5,
    marginLeft: "10px",
    marginBottom: "4px",

    fontSize: 15,
    color: "white",
    lineHeight: "18px",
  },
  backdrop: {
    position: "absolute",
    zIndex: 1300,
    background: "rgba(36, 50, 58, 0.8)",
  }
}))
const validationSchema = Yup.object().shape({
  name: Yup.string().required(getTrans('forms.Обязательно к заполнению')),
  email: Yup.string()
    .email(getTrans('forms.Некорректный email'))
    .required(getTrans('forms.Обязательно к заполнению'))
    .test(
      "not-bot-email",
      getTrans('forms.Некорректный email'),
      (value) => !isBotEmail(value)
    ),
  message: Yup.string().required(getTrans('forms.Обязательно к заполнению')),
});

export default FormFeedback
